import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { KpiCard, DashboardHeader, SectionTable, formatINR } from "../components/DashboardWidgets";
import { Receipt, CurrencyInr, Warning, FileText, ClockClockwise } from "@phosphor-icons/react";

const KPI = [
  { key: "today_billing",     label: "Today's Billing",   icon: Receipt,        tone: "blue",    money: true },
  { key: "month_billing",     label: "Month Billing",     icon: Receipt,        tone: "indigo",  money: true },
  { key: "total_billing",     label: "Total Billing",     icon: Receipt,        tone: "slate",   money: true },
  { key: "today_collection",  label: "Today's Collection",icon: CurrencyInr,    tone: "emerald", money: true },
  { key: "month_collection",  label: "Month Collection",  icon: CurrencyInr,    tone: "emerald", money: true },
  { key: "total_collected",   label: "Total Collected",   icon: CurrencyInr,    tone: "emerald", money: true },
  { key: "outstanding",       label: "Outstanding",       icon: Warning,        tone: "amber",   money: true },
  { key: "gst_collected",     label: "GST Collected",     icon: FileText,       tone: "indigo",  money: true },
  { key: "overdue_amount",    label: "Overdue Amount",    icon: Warning,        tone: "rose",    money: true },
  { key: "overdue_count",     label: "Overdue Invoices",  icon: ClockClockwise, tone: "rose",    money: false },
  { key: "invoices_count",    label: "Total Invoices",    icon: FileText,       tone: "blue",    money: false },
  { key: "payments_count",    label: "Payments Logged",   icon: CurrencyInr,    tone: "blue",    money: false },
];

const FinanceDashboard = ({ user, logout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/dashboard/finance`); setData(r.data); }
    catch { toast.error("Failed to load Finance dashboard"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <DashboardHeader overline="Finance" title="Finance Dashboard" subtitle="Billing, collections, GST, outstanding & overdue receivables — all from MongoDB." onRefresh={load} refreshTestId="finance-refresh-btn" />
          {loading ? <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div> : (
            <>
              <div data-testid="finance-kpi-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
                {KPI.map(k => <KpiCard key={k.key} icon={k.icon} label={k.label} tone={k.tone} money={k.money} value={data?.kpis?.[k.key]} testId={`finance-kpi-${k.key}`} />)}
              </div>
              <SectionTable
                title="Overdue Invoices" icon={Warning} testId="finance-overdue"
                columns={[{ label: "Invoice #" }, { label: "Customer" }, { label: "Total", align: "right" }, { label: "Paid", align: "right" }, { label: "Outstanding", align: "right" }, { label: "Due Date" }]}
                rows={data?.overdue_invoices || []}
                emptyText="No overdue invoices."
                renderRow={(r, i) => (
                  <tr key={`${r.doc_number}-${i}`} data-testid={`finance-overdue-row-${i}`} className="border-b border-slate-100">
                    <td className="p-3 font-mono text-xs text-slate-700">{r.doc_number || "—"}</td>
                    <td className="p-3 text-sm">{r.customer_name}</td>
                    <td className="p-3 text-right tabular-nums">{formatINR(r.total)}</td>
                    <td className="p-3 text-right tabular-nums text-emerald-700">{formatINR(r.paid)}</td>
                    <td className="p-3 text-right tabular-nums text-rose-700 font-semibold">{formatINR(r.outstanding)}</td>
                    <td className="p-3 text-xs text-slate-500">{r.due_date || "—"}</td>
                  </tr>
                )}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinanceDashboard;
